import React from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  DataTable,
  DataTableCellVariants,
  Highlight,
  Icon,
  Pagination,
  Tooltip,
  type DataTableHeaderProps,
  type DataTableRowData,
  type RowsPerPageType,
} from '@node-space/ui-components'
import { DateFormat } from 'constants/General'
import type { CardTransaction } from 'services/CardService'
import {
  formatCurrency,
  getStatusTranslationKey,
  getStatusType,
  getUsdAmount,
} from '../helpers/cards'

interface CardTransactionsDataTableProps {
  transactions: CardTransaction[]
  page: number
  rowsPerPage: RowsPerPageType
  totalElements: number
  onChangePage: (page: number) => void
  onChangeRowsPerPage: (rowsPerPage: RowsPerPageType) => void
}

export const CardTransactionsDataTable = ({
  transactions,
  page,
  rowsPerPage,
  totalElements,
  onChangePage,
  onChangeRowsPerPage,
}: CardTransactionsDataTableProps) => {
  const { t } = useTranslation()

  const headers: DataTableHeaderProps[] = [
    {
      label: t('customers.cardTransactionsTable.transactionId'),
      key: 'transactionId',
      variant: DataTableCellVariants.TEXT,
    },
    {
      label: t('customers.cardTransactionsTable.merchant'),
      key: 'merchant',
      variant: DataTableCellVariants.TEXT,
    },
    {
      label: t('customers.cardTransactionsTable.localAmount'),
      key: 'localAmount',
      variant: DataTableCellVariants.TEXT,
    },
    {
      label: t('customers.cardTransactionsTable.usdAmount'),
      key: 'usdAmount',
      variant: DataTableCellVariants.TEXT,
    },
    {
      label: t('customers.cardTransactionsTable.date'),
      key: 'date',
      variant: DataTableCellVariants.TEXT,
    },
    {
      label: t('customers.cardTransactionsTable.status'),
      key: 'status',
      variant: DataTableCellVariants.HIGHLIGHT,
    },
    {
      label: t('customers.cardTransactionsTable.reason'),
      key: 'reason',
      variant: DataTableCellVariants.TEXT,
    },
    { label: '', key: 'actions', variant: DataTableCellVariants.TEXT },
  ]

  const tableData: DataTableRowData[] = transactions.map((transaction: CardTransaction) => ({
    id: transaction.id,
    cells: [
      {
        key: 'transactionId',
        variant: DataTableCellVariants.TEXT,
        Text: transaction.id ? `${transaction.id.slice(0, 8)}...${transaction.id.slice(-7)}` : '-',
      },
      {
        key: 'merchant',
        variant: DataTableCellVariants.TEXT,
        Text: transaction.metadata?.merchantName || '-',
      },
      {
        key: 'localAmount',
        variant: DataTableCellVariants.TEXT,
        Text:
          transaction.metadata?.localAmount && transaction.metadata?.localCurrency
            ? formatCurrency(transaction.metadata.localAmount, transaction.metadata.localCurrency)
            : formatCurrency(
                transaction.settledAmount || transaction.authorizedAmount,
                transaction.asset
              ),
      },
      {
        key: 'usdAmount',
        variant: DataTableCellVariants.TEXT,
        Text: getUsdAmount(transaction),
      },
      {
        key: 'date',
        variant: DataTableCellVariants.TEXT,
        Text: dayjs(transaction.createdAt).format(DateFormat.DD_MMMM_YYYY),
      },
      {
        key: 'status',
        variant: DataTableCellVariants.HIGHLIGHT,
        Highlights: [
          <Highlight key={transaction.status} type={getStatusType(transaction.status)}>
            {t(getStatusTranslationKey(transaction.status))}
          </Highlight>,
        ],
      },
      {
        key: 'reason',
        variant: DataTableCellVariants.TEXT,
        Text: '',
        ...(transaction.formattedDeclineReason && {
          Actions: [
            <Tooltip
              key="reason-tooltip"
              bodyContent={transaction.formattedDeclineReason}
              type="simple"
              position="bottom"
            >
              <Button variant="ghost" iconElement={<Icon name="Info" />} />
            </Tooltip>,
          ],
        }),
      },
      {
        key: 'actions',
        variant: DataTableCellVariants.TEXT,
        Text: '',
      },
    ],
  }))

  return (
    <>
      <DataTable name="CardTransactionsTable" headers={headers} data={tableData} />
      <Box paddingT={16}>
        <Pagination
          testId="customer-card-transactions-pagination"
          page={page}
          rowsPerPage={rowsPerPage}
          total={totalElements}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
        />
      </Box>
    </>
  )
}
